import { QuoteEditData } from '../hooks/useQuoteEdit';
import { InvoiceEditData } from '../hooks/useInvoiceEdit';
import { useCompanyStore } from '../stores/companyStore';
import { logger } from './logger';

/**
 * Génère une date au format yyyy-MM-dd pour les inputs HTML
 */
function getTodayFormatted(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Génère une date dans 30 jours au format yyyy-MM-dd
 */
function get30DaysLater(): string {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Crée des données vides pour un nouveau devis avec les infos de l'entreprise
 */
export function createEmptyQuoteData(): QuoteEditData {
  const { company } = useCompanyStore.getState();

  logger.log('🔍 createEmptyQuoteData - company:', company);

  return {
    // Pas d'ID car nouveau document
    quoteNumber: '', // Sera généré par le backend
    date: getTodayFormatted(),
    validUntil: get30DaysLater(),

    // Client - vide
    clientName: '',
    clientCompany: '',
    clientEmail: '',
    clientAddressLine1: '',
    clientCity: '',
    clientZipCode: '',
    clientCountry: 'FR',
    clientPhone: '',
    clientVatNumber: '',

    // Items - un item vide par défaut
    items: [
      {
        id: '1',
        description: 'Nouvelle prestation',
        quantity: 1,
        unitPrice: 0,
        taxPercent: 20,
        total: 0,
      },
    ],

    // Totaux initiaux
    subtotal: 0,
    tax: 0,
    total: 0,

    // Remise
    discountType: 'none',
    discountValue: 0,
    discountAmount: 0,

    // Notes & Titre (alignés sur Prisma)
    title: '',
    internalNotes: '',
    termsConditions: '',

    // Snapshot de l'entreprise (données actuelles)
    companyName: company?.legalName || company?.tradeName || '',
    companyAddressLine1: company?.addressLine1 || '',
    companyCity: company?.city || '',
    companyZipCode: company?.zipCode || '',
    companyCountry: company?.countryCode || 'FR',
    companyPhone: company?.phone || '',
    companyEmail: company?.email || '',
    companySiret: company?.registrationNumber || '',
    companyVatNumber: company?.vatNumber || '',
  };
}

/**
 * Crée des données vides pour une nouvelle facture avec les infos de l'entreprise
 */
export function createEmptyInvoiceData(): InvoiceEditData {
  const { company } = useCompanyStore.getState();

  return {
    // Pas d'ID car nouveau document
    invoiceNumber: '', // Sera généré par le backend
    date: getTodayFormatted(),
    dueDate: get30DaysLater(),

    // Client - vide
    clientName: '',
    clientCompany: '',
    clientEmail: '',
    clientAddressLine1: '',
    clientCity: '',
    clientZipCode: '',
    clientCountry: 'FR',
    clientPhone: '',
    clientVatNumber: '',

    // Items - un item vide par défaut
    items: [
      {
        id: '1',
        description: 'Nouvelle prestation',
        quantity: 1,
        unitPrice: 0,
        taxPercent: 20,
        total: 0,
      },
    ],

    // Totaux initiaux
    subtotal: 0,
    tax: 0,
    total: 0,

    // Remise
    discountType: 'none',
    discountValue: 0,
    discountAmount: 0,

    // Notes & Titre (alignés sur Prisma)
    title: '',
    internalNotes: '',
    termsConditions: '',

    // Informations de paiement (OBLIGATOIRE pour Factur-X EN 16931)
    paymentMethod: company?.defaultPaymentMethod || 'virement', // Récupéré des paramètres société

    // Snapshot de l'entreprise (données actuelles)
    companyName: company?.legalName || company?.tradeName || '',
    companyAddressLine1: company?.addressLine1 || '',
    companyCity: company?.city || '',
    companyZipCode: company?.zipCode || '',
    companyCountry: company?.countryCode || 'FR',
    companyPhone: company?.phone || '',
    companyEmail: company?.email || '',
    companySiret: company?.registrationNumber || '',
    companyVatNumber: company?.vatNumber || '',
  };
}
